/**
 * Job-related constants and configuration options
 * Centralized location for all job filter options to maintain consistency
 */

export const ROLE_OPTIONS = [
  // Art Department
  { value: 'category-art-department', label: '**Art Department**', isCategory: true },
  { value: 'art-director', label: 'Art Director' },
  { value: 'construction-coordinator', label: 'Construction Coordinator' },
  { value: 'production-designer', label: 'Production Designer' },
  { value: 'props-master', label: 'Props Master' },
  { value: 'scenic-painter', label: 'Scenic Painter' },
  { value: 'set-designer', label: 'Set Designer' },
  { value: 'set-dresser', label: 'Set Dresser' },
  
  // Camera Department
  { value: 'category-camera-department', label: '**Camera Department**', isCategory: true },
  { value: 'first-assistant-camera', label: '1st Assistant Camera (1st AC / Focus Puller)' },
  { value: 'second-assistant-camera', label: '2nd Assistant Camera (2nd AC / Clapper Loader)' },
  { value: 'camera-operator', label: 'Camera Operator' },
  { value: 'digital-imaging-technician', label: 'Digital Imaging Technician (DIT)' },
  { value: 'dp-cinematographer', label: 'Director of Photography (DP) / Cinematographer' },
  { value: 'drone-operator', label: 'Drone Operator' },
  { value: 'steadicam-gimbal-operator', label: 'Steadicam / Gimbal Operator' },
  
  // Casting & Talent
  { value: 'category-casting-talent', label: '**Casting & Talent**', isCategory: true },
  { value: 'actor', label: 'Actor' },
  { value: 'casting-director', label: 'Casting Director' },
  { value: 'choreographer', label: 'Choreographer (Dance/Fight)' },
  { value: 'extras-casting', label: 'Extras Casting' },
  { value: 'intimacy-coordinator', label: 'Intimacy Coordinator' },
  { value: 'stand-in-photo-double', label: 'Stand-In / Photo Double' },
  { value: 'stunt-coordinator', label: 'Stunt Coordinator' },
  { value: 'stunt-performer', label: 'Stunt Performer' },
  
  // Costume, Hair & Makeup
  { value: 'category-costume-hair-makeup', label: '**Costume, Hair & Makeup**', isCategory: true },
  { value: 'costume-designer', label: 'Costume Designer' },
  { value: 'hair-stylist', label: 'Hair Stylist' },
  { value: 'makeup-artist', label: 'Makeup Artist' },
  { value: 'special-effects-makeup', label: 'Special Effects Makeup' },
  { value: 'wardrobe-assistant', label: 'Wardrobe Assistant' },
  { value: 'wardrobe-supervisor', label: 'Wardrobe Supervisor' },
  
  // Grip & Electric (G&E)
  { value: 'category-grip-electric', label: '**Grip & Electric (G&E)**', isCategory: true },
  { value: 'best-boy-electric', label: 'Best Boy Electric' },
  { value: 'best-boy-grip', label: 'Best Boy Grip' },
  { value: 'dolly-grip', label: 'Dolly Grip' },
  { value: 'electrician', label: 'Electrician' },
  { value: 'gaffer', label: 'Gaffer (Chief Lighting Technician)' },
  { value: 'grip', label: 'Grip' },
  { value: 'key-grip', label: 'Key Grip' },
  
  // Other Key Roles
  { value: 'category-other-key-roles', label: '**Other Key Roles**', isCategory: true },
  { value: 'location-manager', label: 'Location Manager' },
  { value: 'location-scout', label: 'Location Scout' },
  { value: 'still-photographer', label: 'Still Photographer / On-Set Photographer' },
  { value: 'unit-publicist', label: 'Unit Publicist' },
  { value: 'videographer', label: 'Videographer (BTS, EPK)' },
  
  // Post-Production
  { value: 'category-post-production', label: '**Post-Production**', isCategory: true },
  { value: 'adr-recordist', label: 'ADR Recordist' },
  { value: 'assistant-editor', label: 'Assistant Editor' },
  { value: 'colorist', label: 'Colorist' },
  { value: 'composer', label: 'Composer (Music)' },
  { value: 'editor', label: 'Editor' },
  { value: 'foley-artist', label: 'Foley Artist' },
  { value: 'motion-graphics-designer', label: 'Motion Graphics Designer' },
  { value: 'music-supervisor', label: 'Music Supervisor' },
  { value: 'sound-designer', label: 'Sound Designer (Post)' },
  { value: 'vfx-artist', label: 'VFX Artist' },
  { value: 'visual-effects-supervisor', label: 'Visual Effects Supervisor' },
  
  // Production
  { value: 'category-production', label: '**Production**', isCategory: true },
  { value: 'assistant-director', label: 'Assistant Director (1st AD, 2nd AD)' },
  { value: 'director', label: 'Director' },
  { value: 'line-producer', label: 'Line Producer' },
  { value: 'producer', label: 'Producer' },
  { value: 'production-assistant', label: 'Production Assistant (PA)' },
  { value: 'production-coordinator', label: 'Production Coordinator' },
  { value: 'script-supervisor', label: 'Script Supervisor' },
  { value: 'unit-production-manager', label: 'Unit Production Manager (UPM)' },
  
  // Sound Department
  { value: 'category-sound-department', label: '**Sound Department**', isCategory: true },
  { value: 'boom-operator', label: 'Boom Operator' },
  { value: 'production-sound-mixer', label: 'Production Sound Mixer' },
  { value: 'utility-sound-technician', label: 'Utility Sound Technician' },
] as const

export const PROJECT_TYPE_OPTIONS = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'documentary', label: 'Documentary' },
  { value: 'feature-film', label: 'Feature film' },
  { value: 'short-film', label: 'Short film' },
  { value: 'student-film', label: 'Student film' },
  { value: 'web-series', label: 'Web series' },
  { value: 'youtube-video', label: 'Youtube video' },
] as const

export const DATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Dates' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Next Week' },
  { value: 'month', label: 'Next Month' },
  { value: '3months', label: 'Next 3 Months' },
] as const

// Location mapping for consistent display names
export const LOCATION_MAP: Record<string, string> = {
  remote: 'Remote',
  atlanta: 'Atlanta, GA',
  austin: 'Austin, TX',
  baltimore: 'Baltimore, MD',
  boston: 'Boston, MA',
  charlotte: 'Charlotte, NC',
  chicago: 'Chicago, IL',
  columbus: 'Columbus, OH',
  dallas: 'Dallas, TX',
  denver: 'Denver, CO',
  'fort-worth': 'Fort Worth, TX',
  houston: 'Houston, TX',
  indianapolis: 'Indianapolis, IN',
  jacksonville: 'Jacksonville, FL',
  'los-angeles': 'Los Angeles, CA',
  miami: 'Miami, FL',
  'new-york': 'New York, NY',
  philadelphia: 'Philadelphia, PA',
  phoenix: 'Phoenix, AZ',
  'san-antonio': 'San Antonio, TX',
  'san-diego': 'San Diego, CA',
  'san-francisco': 'San Francisco, CA',
  'san-jose': 'San Jose, CA',
  seattle: 'Seattle, WA',
} as const

export type RoleValue = (typeof ROLE_OPTIONS)[number]['value']
export type ProjectTypeValue = (typeof PROJECT_TYPE_OPTIONS)[number]['value']
export type DateFilterValue = (typeof DATE_FILTER_OPTIONS)[number]['value']

// Role option interface
export interface RoleOption {
  value: string
  label: string
  isCategory?: boolean
}
